import { CloseIcon } from '@chakra-ui/icons';
import { Box, Button, chakra, Flex, HStack, Input, Text, VStack } from '@chakra-ui/react';
import { ChangeEvent, forwardRef, useCallback, useState } from 'react';
import { UploadIcon } from '~components/ui/icons/UploadIcon';

type Props = {
  onChange: (file: File | null) => void;
  onError: (error: Error) => void;
  fileName?: string;
  title?: string;
  clearLabel?: string;
  confirmToDelete?: boolean;
};

const ALLOWED_FILE_TYPES = ['text/csv', 'application/vnd.ms-excel'];

export const CSVInput = forwardRef<HTMLInputElement, Props>(function CSVInput(
  { onChange, onError, fileName, title },
  ref,
) {
  const [selectedFile, setSelectedFile] = useState<File | undefined>();

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      if (!ALLOWED_FILE_TYPES.includes(file.type)) {
        onError(
          new Error(
            `Invalid image selected. The type "${
              file.type.split('/')[1] ?? file.type
            }" is not supported.`,
          ),
        );
        return;
      }

      setSelectedFile(file);
      onChange(file);
    },
    [onChange, onError],
  );

  const clearFile = useCallback(() => {
    setSelectedFile(undefined);
    onChange(null);
    if (ref && typeof ref !== 'function' && ref?.current) {
      ref.current.value = '';
    }
  }, [onChange, ref]);

  return (
    <Flex direction="column">
      <VStack align="start" spacing={2}>
        {title && <Text fontWeight="500">{title}</Text>}

        <HStack spacing={3}>
          <chakra.label
            display="flex"
            alignItems="center"
            gap={2}
            paddingY={1.5}
            paddingX={10}
            borderColor={selectedFile ? 'gray.500' : '#146BBD'}
            borderRadius="md"
            borderWidth="1px"
            color={selectedFile ? 'gray.500' : '#146BBD'}
            fontWeight="medium"
            _hover={{ backgroundColor: 'gray.100' }}
            cursor={selectedFile ? 'not-allowed' : 'pointer'}
            backgroundColor="transparent"
            opacity={selectedFile ? 0.6 : 1}
            pointerEvents={selectedFile ? 'none' : 'auto'}
          >
            <Input
              ref={ref}
              type="file"
              onChange={handleChange}
              variant="unstyled"
              disabled={!!selectedFile}
              accept=".csv"
              css={{ opacity: 0, width: '0.1px', height: '0.1px', position: 'absolute' }}
            />
            <UploadIcon width="5" height="5" />
            CSV File
          </chakra.label>
          {selectedFile?.name || fileName ? (
            <Box color="gray.700" pl={4} fontSize="md" noOfLines={1} maxW="200px">
              {selectedFile?.name || fileName}
            </Box>
          ) : null}
          {selectedFile || fileName ? (
            <>
              <Button size="sm" variant="inline" colorScheme="gray" onClick={clearFile}>
                <CloseIcon color="gray.500" width="3" height="3" />
              </Button>
            </>
          ) : null}
        </HStack>
      </VStack>
    </Flex>
  );
});
